import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, MessageCircle, ArrowUpRight } from 'lucide-react';
import { site } from '../data/site';

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="brand-logo" className="group flex items-center gap-3">
    <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-[inset_0_0_0_1px_rgba(6,75,43,0.25)] transition-transform duration-300 group-hover:-rotate-6">
      <img src="/thebighub-mark.png" alt="TheBigHub logo" className="h-full w-full object-contain" />
    </span>
    <span className="leading-tight">
      <span className="block font-display text-xl font-bold tracking-tight">
        <span className={dark ? 'text-white' : 'text-ink'}>The</span>
        <span className="bg-gradient-to-r from-rose-400 to-violet-400 bg-clip-text text-transparent">Big</span>
        <span className="bg-gradient-to-r from-sky-400 to-emerald-400 bg-clip-text text-transparent">Hub</span>
      </span>
      <span className={`block font-mono text-[9px] uppercase tracking-[0.24em] ${dark ? 'text-goldlight' : 'text-forest'}`}>
        Your Digital Growth Partner
      </span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => (document.body.style.overflow = '');
  }, [open]);

  const onDark = !scrolled && location.pathname.startsWith('/services/');

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled ? 'border-b border-ink/10 bg-paper/90 shadow-[0_10px_40px_-20px_rgba(17,17,17,0.25)] backdrop-blur-xl' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo dark={onDark} />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {site.nav.slice(1).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors duration-300 hover:text-gold ${
                    isActive ? (onDark ? 'text-goldlight' : 'text-forest') : onDark ? 'text-white' : 'text-ink'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={site.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              aria-label="Chat with TheBigHub on WhatsApp"
              className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-300 ${
                onDark ? 'border-white/25 text-white hover:border-gold hover:text-goldlight' : 'border-ink/15 text-ink hover:border-forest hover:text-forest'
              }`}
            >
              <MessageCircle size={18} />
            </a>
            <Link to="/contact" data-testid="nav-cta-button" className={onDark ? 'btn-mint' : 'btn-gold'}>
              Let's Grow Your Business
              <ArrowUpRight size={17} />
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            aria-label="Open navigation menu"
            className={`flex h-11 w-11 items-center justify-center rounded-full border lg:hidden ${
              onDark ? 'border-white/25 text-white' : 'border-ink/15 text-ink'
            }`}
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-pine"
          >
            <div className="flex h-20 items-center justify-between px-5 sm:px-8">
              <Logo dark />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close navigation menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobile">
              {site.nav.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-display text-3xl font-bold transition-colors sm:text-4xl ${
                        isActive ? 'text-goldlight' : 'text-white hover:text-goldlight'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-col gap-3 px-8 pb-10"
            >
              <a href={site.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-button" className="btn-outline-light w-full">
                <MessageCircle size={18} /> Talk to Us on WhatsApp
              </a>
              <Link to="/contact" data-testid="mobile-cta-button" className="btn-mint w-full">
                Let's Grow Your Business
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
